import type { JobContext } from './DrawerContext'
import {
  BellIcon,
  BookUserIcon,
  LayoutDashboardIcon,
  MapPinnedIcon,
  PaletteIcon,
  SettingsIcon,
  ShieldCheckIcon,
  UserCheckIcon,
  UserIcon,
  UsersIcon,
  type LucideIcon,
} from 'lucide-react'

export type UserRole = 'admin' | 'arborist' | 'client'

export interface NavItem {
  to: string
  label: string
  icon?: LucideIcon
  /** Visible to every role when omitted */
  roles?: UserRole[]
  /** This item's row carries the unread-notification count chip */
  showsUnreadBadge?: boolean
}

/**
 * Single source of truth for navigation menus. Each list renders on two
 * surfaces (desktop and mobile) — add items here, never inline in a layout.
 */

/** Primary navigation — desktop top bar and mobile left drawer (MobileDrawer) */
export const primaryNavItems: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboardIcon, roles: ['admin', 'arborist'] },
  { to: '/teams', label: 'Teams', icon: UsersIcon, roles: ['admin', 'arborist'] },
  { to: '/clients', label: 'Clients', icon: BookUserIcon, roles: ['admin', 'arborist'] },
  { to: '/branding', label: 'Branding', icon: PaletteIcon, roles: ['admin', 'arborist'] },
  // '/reports' deliberately absent: reports are created per-job; a cross-job
  // delivery/sign-off tracking page may earn the slot back later
  { to: '/admin', label: 'Admin', icon: ShieldCheckIcon, roles: ['admin'] },
  { to: '/admin/access-requests', label: 'Access Requests', icon: UserCheckIcon, roles: ['admin'] },
]

/** Account menu — desktop avatar dropdown and mobile right drawer (UserDrawer) */
export const userMenuItems: NavItem[] = [
  { to: '/notifications', label: 'Notifications', icon: BellIcon, showsUnreadBadge: true },
  { to: '/profile', label: 'Profile', icon: UserIcon },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
  { to: '/offline-maps', label: 'Offline Maps', icon: MapPinnedIcon },
]

export const visibleNavItems = (items: NavItem[], role: UserRole | null): NavItem[] =>
  items.filter((item) => !item.roles || (role && item.roles.includes(role)))

type JobDrawerActionBase = {
  key: string
  label: string
  variant: 'default' | 'outline' | 'secondary'
}

export type JobDrawerAction =
  | (JobDrawerActionBase & { to: string })
  | (JobDrawerActionBase & { onClick: () => void })

/**
 * Job actions shown in the mobile drawer while viewing a job, in display
 * order. Callback-backed actions only appear when JobDetail provided the
 * handler (i.e. the user can edit the job); Reports is a plain link and is
 * always included.
 */
export const getJobDrawerActions = (jobContext: JobContext): JobDrawerAction[] => {
  const actions: JobDrawerAction[] = []
  if (jobContext.onViewFiles) {
    actions.push({
      key: 'files',
      label: 'Job Files',
      variant: 'default',
      onClick: jobContext.onViewFiles,
    })
  }
  actions.push({
    key: 'reports',
    // "Create New Report" (matching the target page heading) rather than
    // "Reports", which collides with the global Reports nav item.
    label: 'Create New Report',
    variant: 'default',
    to: `/jobs/${jobContext.jobId}/reports`,
  })
  if (jobContext.onDownloadMaps) {
    actions.push({
      key: 'download-maps',
      label: 'Download Offline Maps',
      variant: 'outline',
      onClick: jobContext.onDownloadMaps,
    })
  }
  if (jobContext.onEditJob) {
    actions.push({
      key: 'edit-job',
      label: 'Edit Job Details',
      variant: 'secondary',
      onClick: jobContext.onEditJob,
    })
  }
  return actions
}
